import React, { useState } from 'react';
import { Sparkles, Edit3, Loader, CheckCircle } from 'lucide-react';

export const BlogGeneratorTab = () => {
  const [topic, setTopic] = useState('');
  const [keyword, setKeyword] = useState('');
  const [language, setLanguage] = useState('sq');
  const [generating, setGenerating] = useState(false);
  const [generatedBlog, setGeneratedBlog] = useState<any>(null);
  const [publishing, setPublishing] = useState(false);
  const [publishSuccess, setPublishSuccess] = useState(false);

  const handleGenerate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!topic) return;
    setGenerating(true);
    setGeneratedBlog(null);
    setPublishSuccess(false);

    try {
      const res = await fetch('/api/ai/generate-blog', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ topic, keyword, language })
      });
      if (!res.ok) {
        let msg = "Generation failed";
        try { const d = await res.json(); msg = d.error || msg; } catch(e){}
        throw new Error(msg);
      }
      const data = await res.json();
      setGeneratedBlog(data);
    } catch (err: any) {
      alert("Error generating blog: " + err.message);
    } finally {
      setGenerating(false);
    }
  };

  const handlePublish = async () => {
    if (!generatedBlog) return;
    setPublishing(true);
    try {
      // Create JSON payload matching blogContent structure
      const newBlogEntry = {
        title: generatedBlog.title,
        date: new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }),
        tag: generatedBlog.tag,
        image: "https://images.unsplash.com/photo-1498050108023-c5249f4df085?q=60&w=640&auto=format&fit=crop", // Placeholder if we don't generate images automatically
        content: generatedBlog.content,
        faq: generatedBlog.faq,
        metaDescription: generatedBlog.metaDescription,
        keywords: generatedBlog.keywords,
        slug: generatedBlog.slug
      };

      const res = await fetch('/api/github/publish-blog', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          language,
          slug: generatedBlog.slug || Date.now().toString(),
          content: newBlogEntry
        })
      });

      if (!res.ok) {
        let msg = "Publish failed";
        try { const d = await res.json(); msg = d.error || msg; } catch(e){}
        throw new Error(msg);
      }

      setPublishSuccess(true);
      setTimeout(() => {
        setGeneratedBlog(null);
        setTopic('');
        setKeyword('');
      }, 3000);
    } catch (err: any) {
      alert("Error publishing to GitHub: " + err.message);
    } finally {
      setPublishing(false);
    }
  };

  return (
    <div className="space-y-8">
      <div className="glass-card p-8 rounded-3xl border border-white/5">
        <h3 className="text-2xl font-bold font-display mb-2 flex items-center gap-2">
          <Sparkles className="text-primary-light" /> AI Blog Generator
        </h3>
        <p className="text-white/60 text-sm mb-6">Automatically write, SEO-optimize, and publish new blog articles via GitHub.</p>

        <form onSubmit={handleGenerate} className="space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-bold text-white/80 mb-2">Blog Topic</label>
              <input
                type="text"
                required
                value={topic}
                onChange={e => setTopic(e.target.value)}
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:border-primary-light outline-none"
                placeholder="e.g. The importance of fast web design in 2026"
              />
            </div>
            <div>
              <label className="block text-sm font-bold text-white/80 mb-2">Target Keyword (Optional)</label>
              <input
                type="text"
                value={keyword}
                onChange={e => setKeyword(e.target.value)}
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:border-primary-light outline-none"
                placeholder="e.g. web design Kosovo"
              />
            </div>
            <div>
              <label className="block text-sm font-bold text-white/80 mb-2">Language</label>
              <select
                value={language}
                onChange={e => setLanguage(e.target.value)}
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:border-primary-light outline-none appearance-none"
              >
                <option value="sq">Albanian (sq)</option>
                <option value="en">English (en)</option>
                <option value="de">German (de)</option>
              </select>
            </div>
          </div>
          
          <button
            type="submit"
            disabled={generating}
            className="btn-primary py-3 px-6 rounded-xl font-bold flex items-center gap-2"
          >
            {generating ? <><Loader className="animate-spin" size={18} /> Generating...</> : <><Sparkles size={18} /> Generate with AI</>}
          </button>
        </form>
      </div>

      {generatedBlog && (
        <div className="glass-card p-8 rounded-3xl border border-white/5 space-y-6">
          <div className="flex justify-between items-center border-b border-white/10 pb-4">
            <h3 className="text-xl font-bold flex items-center gap-2"><Edit3 size={18}/> Preview & Edit</h3>
            <button
              onClick={handlePublish}
              disabled={publishing}
              className="bg-green-500 hover:bg-green-600 text-white py-2 px-6 rounded-xl font-bold transition-all flex items-center gap-2 disabled:opacity-50"
            >
              {publishing ? <Loader className="animate-spin" size={16}/> : publishSuccess ? <CheckCircle size={16}/> : null}
              {publishing ? 'Publishing to GitHub...' : publishSuccess ? 'Published!' : 'Publish to GitHub'}
            </button>
          </div>

          <div className="space-y-4">
            <div>
              <label className="text-xs text-white/50 uppercase font-bold">SEO Title (H1)</label>
              <input 
                type="text" 
                value={generatedBlog.title} 
                onChange={e => setGeneratedBlog({...generatedBlog, title: e.target.value})}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2 mt-1 text-lg font-bold"
              />
            </div>
            <div>
              <label className="text-xs text-white/50 uppercase font-bold">Meta Description</label>
              <textarea 
                value={generatedBlog.metaDescription} 
                onChange={e => setGeneratedBlog({...generatedBlog, metaDescription: e.target.value})}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2 mt-1"
                rows={2}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-white/50 uppercase font-bold">URL Slug</label>
                <input 
                  type="text" 
                  value={generatedBlog.slug} 
                  onChange={e => setGeneratedBlog({...generatedBlog, slug: e.target.value})}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2 mt-1"
                />
              </div>
              <div>
                <label className="text-xs text-white/50 uppercase font-bold">Tags</label>
                <input 
                  type="text" 
                  value={generatedBlog.tag} 
                  onChange={e => setGeneratedBlog({...generatedBlog, tag: e.target.value})}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2 mt-1"
                />
              </div>
            </div>

            <div>
              <label className="text-xs text-white/50 uppercase font-bold">Image Prompt</label>
              <p className="bg-white/5 p-3 rounded-lg text-sm italic">{generatedBlog.imagePrompt}</p>
            </div>

            <div>
              <label className="text-xs text-white/50 uppercase font-bold">Content Preview</label>
              <div className="bg-white/5 border border-white/10 rounded-lg p-4 mt-1 space-y-4 max-h-96 overflow-y-auto">
                {generatedBlog.content.map((block: any, i: number) => {
                  if (block.type === 'h2') return <h2 key={i} className="text-xl font-bold text-primary-light mt-4">{block.text}</h2>;
                  if (block.type === 'h3') return <h3 key={i} className="text-lg font-bold text-white mt-2">{block.text}</h3>;
                  return <p key={i} className="text-white/80">{block.text}</p>;
                })}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
